/*
  Header and announcement behavior for House of Thiraa.
  Vanilla JavaScript only: dropdowns, mobile drawer, native search panel, sticky state, and announcements.
*/

(function () {
  'use strict';

  const theme = window.HouseOfThiraa;

  if (!theme) {
    return;
  }

  const TRANSITION_FALLBACK_MS = 260;
  const HOVER_CLOSE_DELAY_MS = 150;
  const HEADER_SCROLL_THRESHOLD = 8;

  class AnnouncementBar {
    constructor(element) {
      this.bar = element;
      this.slides = Array.from(element.querySelectorAll('[data-announcement-slide]'));
      this.closeButton = element.querySelector('[data-announcement-close]');
      this.interval = Number(element.dataset.interval) || 5000;
      this.rotationEnabled = element.dataset.rotationEnabled === 'true';
      this.storageKey = `houseOfThiraa:announcement:${element.dataset.sectionId || 'global'}:closed`;
      this.isReducedMotion = theme.responsive.matches('motionReduce');
      this.timer = null;
      this.activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.cleanup = [];

      if (this.activeIndex < 0) {
        this.activeIndex = 0;
      }

      if (this.isDismissed()) {
        this.bar.hidden = true;
        return;
      }

      this.showSlide(this.activeIndex);
      this.bindEvents();
      this.startRotation();
    }

    bindEvents() {
      this.bind(this.closeButton, 'click', () => this.close());
      this.bind(this.bar, 'mouseenter', () => this.stopRotation());
      this.bind(this.bar, 'mouseleave', () => this.startRotation());
      this.bind(this.bar, 'focusin', () => this.stopRotation());
      this.bind(this.bar, 'focusout', () => this.startRotation());
      this.bind(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.stopRotation();
        } else {
          this.startRotation();
        }
      });
      this.cleanup.push(theme.responsive.onChange('motionReduce', (matches) => {
        this.isReducedMotion = matches;

        if (matches) {
          this.stopRotation();
        } else {
          this.startRotation();
        }
      }));
    }

    bind(element, type, callback, options) {
      if (!element) {
        return;
      }

      element.addEventListener(type, callback, options);
      this.cleanup.push(() => element.removeEventListener(type, callback, options));
    }

    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.storageKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    startRotation() {
      if (!this.rotationEnabled || this.isReducedMotion || this.slides.length <= 1 || document.hidden) {
        return;
      }

      this.stopRotation();
      this.timer = window.setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.slides.length;
        this.showSlide(this.activeIndex);
      }, this.interval);
    }

    stopRotation() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    showSlide(index) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
    }

    close() {
      this.stopRotation();
      this.bar.hidden = true;

      try {
        window.sessionStorage.setItem(this.storageKey, 'true');
      } catch (error) {
        // Browsers may block storage in private contexts; dismissal still works for this view.
      }
    }

    destroy() {
      this.stopRotation();
      this.cleanup.forEach((callback) => callback());
      this.bar.dataset.announcementInitialized = 'false';
    }
  }

  class Header {
    constructor(element) {
      this.header = element;
      this.menuToggle = element.querySelector('[data-menu-toggle]');
      this.mobileMenu = element.querySelector('[data-mobile-menu]');
      this.mobileDrawer = element.querySelector('.mobile-menu__drawer');
      this.menuCloseButtons = Array.from(element.querySelectorAll('[data-menu-close]'));
      this.mobileSubmenuToggles = Array.from(element.querySelectorAll('[data-mobile-submenu-toggle]'));
      this.mobileSearchTriggers = Array.from(element.querySelectorAll('[data-mobile-search-trigger]'));
      this.searchToggle = element.querySelector('[data-search-toggle]');
      this.searchRoot = element.querySelector('[data-header-search]');
      this.searchPanel = element.querySelector('[data-search-panel]');
      this.searchBackdrop = element.querySelector('[data-search-backdrop]');
      this.searchInput = element.querySelector('[data-search-input]');
      this.searchClear = element.querySelector('[data-search-clear]');
      this.searchForm = element.querySelector('[data-search-form]');
      this.predictiveSearch = element.querySelector('[data-predictive-search]');
      this.dropdownToggles = Array.from(element.querySelectorAll('[data-dropdown-toggle]'));
      this.cartLink = element.querySelector('[data-cart-trigger]');
      this.cartCount = element.querySelector('[data-cart-count]');
      this.mobileCartLinks = Array.from(element.querySelectorAll('[data-mobile-cart-trigger]'));
      this.mobileCartCounts = Array.from(element.querySelectorAll('[data-mobile-cart-count]'));
      this.dropdownCloseTimers = new WeakMap();
      this.dropdownHideTimers = new WeakMap();
      this.clickedDropdownToggle = null;
      this.searchHideTimer = null;
      this.predictiveSearchTimer = null;
      this.predictiveSearchController = null;
      this.searchOverlayHost = null;
      this.closeMobileFocusTrap = null;
      this.mobileScrollY = 0;
      this.mobileSearchTimer = null;
      this.scrollTicking = false;
      this.lastScrollY = window.scrollY;
      this.lastDirectionalScrollY = this.lastScrollY;
      this.isSticky = element.dataset.sticky === 'true';
      this.isHomepageTransparent = element.dataset.transparentHomepage === 'true' && this.pageCanUseHeroHeader();
      this.isReducedMotion = theme.responsive.matches('motionReduce');
      this.transparentThreshold = 12;
      this.hero = document.querySelector('[data-hero-section]');
      this.cleanup = [];

      this.bindEvents();
      this.updateScrollState();
      this.updateSearchClear();
      this.updateSearchLabel(false);
      this.updateCartCount(this.cartCount ? Number(this.cartCount.textContent) : undefined);
      this.syncMenuOpenClass();
      this.syncWishlistButtons();
      this.syncHeaderWishlist();
      this.syncAccountState();
    }

    bindEvents() {
      if (this.isSticky || this.isHomepageTransparent) {
        const scrollHandler = () => this.requestScrollUpdate();
        window.addEventListener('scroll', scrollHandler, { passive: true });
        this.cleanup.push(() => window.removeEventListener('scroll', scrollHandler));
      }

      if (this.isHomepageTransparent) {
        const resizeHandler = () => {
          this.requestScrollUpdate();
        };
        window.addEventListener('resize', resizeHandler, { passive: true });
        this.cleanup.push(() => window.removeEventListener('resize', resizeHandler));

        this.cleanup.push(theme.events.on('section:load', () => {
          this.hero = document.querySelector('[data-hero-section]');
          this.requestScrollUpdate();
        }));
        this.bindHeroObserver();
      }

      this.cleanup.push(theme.responsive.onChange('motionReduce', (matches) => {
        this.isReducedMotion = matches;
      }));

      this.bind(this.menuToggle, 'click', () => this.toggleMobileMenu());
      this.menuCloseButtons.forEach((button) => this.bind(button, 'click', () => this.closeMobileMenu({ restoreFocus: true })));
      this.bind(this.mobileMenu, 'click', (event) => this.handleMobileMenuClick(event));

      this.mobileSubmenuToggles.forEach((toggle) => {
        this.bind(toggle, 'click', () => this.toggleMobileSubmenu(toggle));
      });
      this.mobileSearchTriggers.forEach((trigger) => this.bind(trigger, 'click', () => this.openSearchFromMobileMenu()));

      this.bind(this.searchToggle, 'click', () => this.toggleSearch());
      this.bind(this.searchBackdrop, 'click', () => this.closeSearch({ restoreFocus: true }));
      this.bind(this.searchPanel, 'click', (event) => this.handleSearchPanelClick(event));
      this.bind(this.searchInput, 'input', () => {
        this.updateSearchClear();
        this.schedulePredictiveSearch();
      });
      this.bind(this.searchClear, 'click', () => this.clearSearch());
      this.bind(this.searchForm, 'submit', (event) => this.handleSearchSubmit(event));
      this.bind(this.predictiveSearch, 'click', (event) => this.handlePredictiveClick(event));
      this.bind(window, 'pageshow', () => {
        this.clearPredictiveSearch();
        if (/\/search\/?$/.test(window.location.pathname)) {
          this.closeSearch({ restoreFocus: false });
          this.setSearchLock(false);
        }
      });

      this.dropdownToggles.forEach((toggle) => {
        const item = toggle.closest('[data-nav-item]');
        const dropdown = this.getDropdown(toggle);

        this.bind(toggle, 'click', () => this.toggleDropdown(toggle));
        this.bind(toggle, 'keydown', (event) => this.handleDropdownKeydown(event, toggle));
        this.bind(dropdown, 'keydown', (event) => this.handleDropdownPanelKeydown(event, toggle));

        if (item) {
          this.bind(item, 'pointerenter', () => this.openDropdown(toggle));
          this.bind(item, 'pointerleave', (event) => this.scheduleDropdownClose(toggle, event.relatedTarget));
          this.bind(dropdown, 'pointerenter', () => this.cancelDropdownClose(toggle));
          this.bind(dropdown, 'pointerleave', (event) => this.scheduleDropdownClose(toggle, event.relatedTarget));
          this.bind(item, 'focusout', (event) => {
            if (!item.contains(event.relatedTarget)) {
              this.closeDropdown(toggle);
            }
          });
        }
      });

      this.bind(document, 'click', (event) => this.handleOutsideClick(event));
      this.bind(this.header, 'click', (event) => this.handleWishlistClick(event));

      this.cleanup.push(theme.events.on('keyboard:escape', () => this.handleEscape()));
      this.cleanup.push(theme.events.on('cart:updated', (event) => {
        const detail = event.detail || {};
        this.updateCartCount(detail.itemCount ?? detail.item_count ?? detail.cart?.item_count);
      }));

      const syncPublishedCart = (event) => {
        const detail = event.detail || {};
        this.updateCartCount(detail.itemCount ?? detail.item_count ?? detail.cart?.item_count);
      };

      document.addEventListener('cart:updated', syncPublishedCart);
      document.addEventListener('cart:change', syncPublishedCart);
      this.cleanup.push(() => {
        document.removeEventListener('cart:updated', syncPublishedCart);
        document.removeEventListener('cart:change', syncPublishedCart);
      });
      this.cleanup.push(theme.events.on('wishlist:updated', () => {
        this.syncWishlistButtons();
        this.syncHeaderWishlist();
      }));
    }

    bind(element, type, callback, options) {
      if (!element) {
        return;
      }

      element.addEventListener(type, callback, options);
      this.cleanup.push(() => element.removeEventListener(type, callback, options));
    }

    requestScrollUpdate() {
      if (this.scrollTicking) {
        return;
      }

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateScrollState();
        this.scrollTicking = false;
      });
    }

    updateScrollState() {
      const currentScrollY = Math.max(window.scrollY, 0);
      const isScrolled = currentScrollY > this.transparentThreshold;
      const scrollDelta = currentScrollY - this.lastDirectionalScrollY;
      const canUseTransparentState = this.canUseTransparentHeader();
      const menuChromeOpen = this.header.classList.contains('is-open');
      const isMenuOpen = this.header.classList.contains('is-menu-open');
      const isOverHero = this.measureOverHero();
      const isTransparent = canUseTransparentState && isOverHero && !isMenuOpen && !menuChromeOpen;
      const isSmallViewport = window.matchMedia('(max-width: 47.99rem)').matches;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hideFloor = isSmallViewport ? Math.round(window.innerHeight * 0.85) : 0;
      const hideThreshold = isSmallViewport ? 28 : HEADER_SCROLL_THRESHOLD;
      const shouldKeepVisible = currentScrollY <= hideFloor || menuChromeOpen || isMenuOpen || reduceMotion;

      if (shouldKeepVisible) {
        this.header.classList.remove('is-scroll-hidden');
        this.lastDirectionalScrollY = currentScrollY;
      } else if (Math.abs(scrollDelta) >= hideThreshold) {
        this.header.classList.toggle('is-scroll-hidden', scrollDelta > 0);
        this.lastDirectionalScrollY = currentScrollY;
      }

      this.header.classList.toggle('is-over-hero', isOverHero && !isMenuOpen && !menuChromeOpen);
      this.header.classList.toggle('is-transparent', isTransparent);
      this.header.classList.toggle('is-scrolled', isScrolled || !isOverHero);
      this.lastScrollY = currentScrollY;
    }

    syncMenuOpenClass() {
      const isMenuOpen = this.dropdownToggles.some((toggle) => {
        const dropdown = this.getDropdown(toggle);
        return toggle.getAttribute('aria-expanded') === 'true' && dropdown?.hasAttribute('data-mega-menu');
      });

      this.header.classList.toggle('is-menu-open', isMenuOpen);
    }

    pageCanUseHeroHeader() {
      return document.body.classList.contains('template-index')
        || document.body.classList.contains('shopify-design-mode')
        || document.documentElement.classList.contains('shopify-design-mode');
    }

    bindHeroObserver() {
      if (!this.isHomepageTransparent || typeof IntersectionObserver !== 'function') {
        return;
      }

      this.hero = document.querySelector('[data-hero-section]');

      if (!this.hero) {
        return;
      }

      const observer = new IntersectionObserver(() => {
        this.requestScrollUpdate();
      }, {
        threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1]
      });

      observer.observe(this.hero);
      this.cleanup.push(() => observer.disconnect());
    }

    measureOverHero() {
      if (this.header.dataset.transparentHomepage !== 'true' || !this.pageCanUseHeroHeader()) {
        return false;
      }

      if (!this.hero || !this.hero.isConnected) {
        this.hero = document.querySelector('[data-hero-section]');
      }

      if (!this.hero) {
        return false;
      }

      const headerHeight = this.header.getBoundingClientRect().height || 68;
      return this.hero.getBoundingClientRect().bottom > headerHeight * 0.45;
    }

    canUseTransparentHeader() {
      return this.header.dataset.transparentHomepage === 'true'
        && this.pageCanUseHeroHeader()
        && Boolean(document.querySelector('[data-hero-section]'));
    }

    isOpen() {
      return this.header.classList.contains('is-open') || this.getOpenDropdownToggle() !== null;
    }

    toggleDropdown(toggle) {
      if (toggle.getAttribute('aria-expanded') === 'true' && this.clickedDropdownToggle === toggle) {
        this.clickedDropdownToggle = null;
        this.closeDropdown(toggle, { restoreFocus: false });
      } else {
        this.clickedDropdownToggle = toggle;
        this.openDropdown(toggle);
      }
    }

    openDropdown(toggle) {
      const dropdown = this.getDropdown(toggle);

      if (!dropdown) {
        return;
      }

      this.cancelDropdownClose(toggle);
      this.cancelDropdownHide(dropdown);
      this.closeSearch({ restoreFocus: false });
      this.closeDropdowns({ exceptToggle: toggle });
      dropdown.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        if (toggle.getAttribute('aria-expanded') === 'true' && !dropdown.hidden) {
          dropdown.classList.add('is-open');
        }
      });
      this.syncMenuOpenClass();
      this.updateScrollState();
    }

    closeDropdown(toggle, options = {}) {
      const dropdown = this.getDropdown(toggle);

      if (!dropdown) {
        return;
      }

      this.cancelDropdownClose(toggle);
      if (this.clickedDropdownToggle === toggle) {
        this.clickedDropdownToggle = null;
      }
      dropdown.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      this.cancelDropdownHide(dropdown);

      if (this.isReducedMotion) {
        dropdown.hidden = true;
      } else {
        const hideTimer = window.setTimeout(() => {
          if (toggle.getAttribute('aria-expanded') !== 'true') {
            dropdown.hidden = true;
          }
          this.dropdownHideTimers.delete(dropdown);
        }, TRANSITION_FALLBACK_MS);
        this.dropdownHideTimers.set(dropdown, hideTimer);
      }

      if (options.restoreFocus) {
        toggle.focus({ preventScroll: true });
      }

      this.syncMenuOpenClass();
      this.updateScrollState();
    }

    closeDropdowns(options = {}) {
      this.dropdownToggles.forEach((toggle) => {
        if (toggle !== options.exceptToggle) {
          this.closeDropdown(toggle, { restoreFocus: false });
        }
      });
    }

    scheduleDropdownClose(toggle, relatedTarget) {
      if (this.clickedDropdownToggle === toggle) {
        return;
      }

      if (this.isDropdownRegionTarget(toggle, relatedTarget)) {
        return;
      }

      this.cancelDropdownClose(toggle);
      const closeTimer = window.setTimeout(() => {
        this.closeDropdown(toggle);
        this.dropdownCloseTimers.delete(toggle);
      }, HOVER_CLOSE_DELAY_MS);
      this.dropdownCloseTimers.set(toggle, closeTimer);
    }

    cancelDropdownClose(toggle) {
      const closeTimer = this.dropdownCloseTimers.get(toggle);

      if (!closeTimer) {
        return;
      }

      window.clearTimeout(closeTimer);
      this.dropdownCloseTimers.delete(toggle);
    }

    cancelDropdownHide(dropdown) {
      const hideTimer = this.dropdownHideTimers.get(dropdown);

      if (!hideTimer) {
        return;
      }

      window.clearTimeout(hideTimer);
      this.dropdownHideTimers.delete(dropdown);
    }

    isDropdownRegionTarget(toggle, target) {
      if (!target) {
        return false;
      }

      const item = toggle.closest('[data-nav-item]');
      const dropdown = this.getDropdown(toggle);
      return Boolean(item?.contains(target) || dropdown?.contains(target));
    }

    getDropdown(toggle) {
      const id = toggle?.getAttribute('aria-controls');
      return id ? this.header.querySelector(`#${CSS.escape(id)}`) : null;
    }

    getOpenDropdownToggle() {
      return this.dropdownToggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true') || null;
    }

    handleDropdownKeydown(event, toggle) {
      if (event.key !== 'ArrowDown') {
        return;
      }

      const dropdown = this.getDropdown(toggle);
      const firstLink = dropdown?.querySelector('a');

      if (!firstLink) {
        return;
      }

      event.preventDefault();
      this.openDropdown(toggle);
      firstLink.focus();
    }

    handleDropdownPanelKeydown(event, toggle) {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      this.closeDropdown(toggle, { restoreFocus: true });
    }

    handleOutsideClick(event) {
      const inHeader = this.header.contains(event.target);
      const inSearch = Boolean(this.searchOverlayHost?.contains(event.target) || this.searchPanel?.contains(event.target));

      if (!inHeader && !inSearch) {
        this.closeDropdowns();
        this.closeSearch({ restoreFocus: false });
      }
    }

    handleEscape() {
      if (this.mobileMenu && !this.mobileMenu.hidden) {
        this.closeMobileMenu({ restoreFocus: true });
        return;
      }

      if (this.searchPanel && !this.searchPanel.hidden) {
        this.closeSearch({ restoreFocus: true });
        return;
      }

      const openDropdown = this.getOpenDropdownToggle();

      if (openDropdown) {
        this.closeDropdown(openDropdown, { restoreFocus: true });
      }
    }

    toggleMobileMenu() {
      if (this.mobileMenu?.hidden) {
        this.openMobileMenu();
      } else {
        this.closeMobileMenu({ restoreFocus: true });
      }
    }

    openMobileMenu() {
      if (!this.mobileMenu || !this.mobileDrawer || !this.menuToggle) {
        return;
      }

      this.closeSearch({ restoreFocus: false });
      this.closeDropdowns();
      this.mobileMenu.hidden = false;
      this.lockMobilePageScroll();
      this.header.classList.add('is-open');
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.updateScrollState();

      window.requestAnimationFrame(() => {
        this.mobileMenu.classList.add('is-open');
        const initialFocus = this.mobileDrawer.querySelector('[data-mobile-menu-close]') || this.mobileDrawer;
        this.closeMobileFocusTrap = theme.a11y.trapFocus(this.mobileDrawer, initialFocus);
      });
    }

    closeMobileMenu(options = {}) {
      if (!this.mobileMenu || this.mobileMenu.hidden || !this.menuToggle) {
        return;
      }

      this.mobileMenu.classList.remove('is-open');
      this.unlockMobilePageScroll();
      this.header.classList.remove('is-open');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.closeMobileSubmenus();

      if (typeof this.closeMobileFocusTrap === 'function') {
        this.closeMobileFocusTrap();
        this.closeMobileFocusTrap = null;
      }

      const finishClose = () => {
        if (!this.mobileMenu.classList.contains('is-open')) {
          this.mobileMenu.hidden = true;
        }
      };

      if (this.isReducedMotion) {
        finishClose();
      } else {
        window.setTimeout(finishClose, TRANSITION_FALLBACK_MS);
      }

      if (options.restoreFocus) {
        this.menuToggle.focus({ preventScroll: true });
      }

      this.updateScrollState();
    }

    handleMobileMenuClick(event) {
      if (event.target.closest('[data-menu-close]')) {
        return;
      }

      if (event.target.closest('a[href]')) {
        this.closeMobileMenu({ restoreFocus: false });
      }
    }

    lockMobilePageScroll() {
      this.mobileScrollY = window.scrollY;
      document.documentElement.classList.add('is-mobile-menu-open');
      document.body.classList.add('header-lock');
      document.body.style.setProperty('--header-lock-scroll-y', `-${this.mobileScrollY}px`);
    }

    unlockMobilePageScroll() {
      if (!document.documentElement.classList.contains('is-mobile-menu-open')) {
        return;
      }

      document.documentElement.classList.remove('is-mobile-menu-open');
      document.body.style.removeProperty('--header-lock-scroll-y');

      if (!document.documentElement.classList.contains('is-search-open')) {
        document.body.classList.remove('header-lock');
      }

      window.requestAnimationFrame(() => window.scrollTo(0, this.mobileScrollY));
    }

    openSearchFromMobileMenu() {
      if (this.mobileSearchTimer) {
        window.clearTimeout(this.mobileSearchTimer);
      }

      this.closeMobileMenu({ restoreFocus: false });
      this.mobileSearchTimer = window.setTimeout(() => {
        this.mobileSearchTimer = null;
        this.openSearch();
      }, this.isReducedMotion ? 0 : TRANSITION_FALLBACK_MS);
    }

    toggleMobileSubmenu(toggle) {
      if (toggle.getAttribute('aria-expanded') === 'true') {
        this.closeMobileSubmenu(toggle);
      } else {
        this.openMobileSubmenu(toggle);
      }
    }

    openMobileSubmenu(toggle) {
      const submenu = this.getMobileSubmenu(toggle);

      if (!submenu) {
        return;
      }

      submenu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');

      if (this.isReducedMotion) {
        submenu.style.maxHeight = 'none';
        return;
      }

      submenu.style.maxHeight = '0px';
      window.requestAnimationFrame(() => {
        submenu.style.maxHeight = `${submenu.scrollHeight}px`;
      });
    }

    closeMobileSubmenu(toggle) {
      const submenu = this.getMobileSubmenu(toggle);

      if (!submenu) {
        return;
      }

      toggle.setAttribute('aria-expanded', 'false');

      if (this.isReducedMotion) {
        submenu.hidden = true;
        submenu.style.maxHeight = '';
        return;
      }

      submenu.style.maxHeight = `${submenu.scrollHeight}px`;
      window.requestAnimationFrame(() => {
        submenu.style.maxHeight = '0px';
      });

      window.setTimeout(() => {
        if (toggle.getAttribute('aria-expanded') !== 'true') {
          submenu.hidden = true;
          submenu.style.maxHeight = '';
        }
      }, TRANSITION_FALLBACK_MS);
    }

    closeMobileSubmenus() {
      this.mobileSubmenuToggles.forEach((toggle) => this.closeMobileSubmenu(toggle));
    }

    getMobileSubmenu(toggle) {
      const id = toggle?.getAttribute('aria-controls');
      return id ? this.header.querySelector(`#${CSS.escape(id)}`) : null;
    }

    toggleSearch() {
      if (this.searchPanel?.hidden) {
        this.openSearch();
      } else {
        this.closeSearch({ restoreFocus: true });
      }
    }

    relocateSearchOverlay() {
      if (this.searchOverlayHost || !this.searchPanel || !this.searchBackdrop) {
        return;
      }

      const host = document.createElement('div');
      host.className = 'header-search-overlay-host';
      host.append(this.searchBackdrop, this.searchPanel);
      document.body.append(host);
      this.searchOverlayHost = host;
    }

    restoreSearchOverlay() {
      if (!this.searchRoot || !this.searchBackdrop || !this.searchPanel) {
        this.searchOverlayHost?.remove();
        this.searchOverlayHost = null;
        return;
      }

      this.searchRoot.append(this.searchBackdrop, this.searchPanel);
      this.searchOverlayHost?.remove();
      this.searchOverlayHost = null;
    }

    setSearchLock(isLocked) {
      document.documentElement.classList.toggle('is-search-open', isLocked);

      if (isLocked) {
        document.body.classList.add('header-lock');
        return;
      }

      if (!(this.mobileMenu && !this.mobileMenu.hidden)) {
        document.body.classList.remove('header-lock');
      }
    }

    handleSearchPanelClick(event) {
      if (event.target === this.searchPanel) {
        this.closeSearch({ restoreFocus: true });
      }
    }

    openSearch() {
      if (!this.searchPanel || !this.searchToggle) {
        return;
      }

      this.closeMobileMenu({ restoreFocus: false });
      this.closeDropdowns();
      this.cancelSearchHide();
      this.relocateSearchOverlay();
      this.searchPanel.hidden = false;
      if (this.searchBackdrop) {
        this.searchBackdrop.hidden = false;
      }
      this.header.classList.add('is-open');
      this.searchToggle.setAttribute('aria-expanded', 'true');
      this.updateSearchLabel(true);
      this.setSearchLock(true);
      this.updateScrollState();

      window.requestAnimationFrame(() => {
        if (!this.searchPanel.hidden) {
          this.searchPanel.classList.add('is-open');
          this.searchBackdrop?.classList.add('is-open');
        }
        this.searchInput?.focus({ preventScroll: true });
      });
    }

    closeSearch(options = {}) {
      if (!this.searchPanel || this.searchPanel.hidden || !this.searchToggle) {
        return;
      }

      this.cancelSearchHide();
      this.searchPanel.classList.remove('is-open');
      this.searchBackdrop?.classList.remove('is-open');
      this.header.classList.remove('is-open');
      this.searchToggle.setAttribute('aria-expanded', 'false');
      this.updateSearchLabel(false);
      this.clearPredictiveSearch();
      this.setSearchLock(false);

      const hidePanel = () => {
        if (this.searchToggle.getAttribute('aria-expanded') !== 'true') {
          this.searchPanel.hidden = true;
          if (this.searchBackdrop) {
            this.searchBackdrop.hidden = true;
          }
        }
        this.searchHideTimer = null;
      };

      if (this.isReducedMotion) {
        hidePanel();
      } else {
        this.searchHideTimer = window.setTimeout(hidePanel, TRANSITION_FALLBACK_MS);
      }

      if (options.restoreFocus) {
        this.searchToggle.focus({ preventScroll: true });
      }

      this.updateScrollState();
    }

    cancelSearchHide() {
      if (!this.searchHideTimer) {
        return;
      }

      window.clearTimeout(this.searchHideTimer);
      this.searchHideTimer = null;
    }

    updateSearchLabel(isOpen) {
      if (!this.searchToggle) {
        return;
      }

      const label = isOpen ? this.searchToggle.dataset.closeLabel : this.searchToggle.dataset.openLabel;

      if (label) {
        this.searchToggle.setAttribute('aria-label', label);
      }
    }

    clearSearch() {
      if (!this.searchInput) {
        return;
      }

      this.searchInput.value = '';
      this.updateSearchClear();
      this.clearPredictiveSearch();
      this.searchInput.focus();
    }

    updateSearchClear() {
      if (!this.searchClear || !this.searchInput) {
        return;
      }

      this.searchClear.hidden = this.searchInput.value.length === 0;
    }

    handleSearchSubmit(event) {
      const query = this.searchInput?.value.trim();

      if (!query) {
        event.preventDefault();
        this.searchInput?.focus();
        return;
      }

      this.clearPredictiveSearch();
      this.closeSearch({ restoreFocus: false });
      this.setSearchLock(false);
    }

    handlePredictiveClick(event) {
      if (event.target.closest('a[href]')) {
        this.closeSearch({ restoreFocus: false });
      }
    }

    extractPredictiveMarkup(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.querySelector('[data-predictive-results], .predictive-search');
      return results ? results.outerHTML : '';
    }

    showPredictiveLoading() {
      if (!this.predictiveSearch) {
        return;
      }

      const root = document.createElement('div');
      root.className = 'predictive-search';
      root.setAttribute('data-predictive-results', '');
      const status = document.createElement('p');
      status.className = 'predictive-search__status';
      status.textContent = this.predictiveSearch.dataset.searchingLabel || 'Searching...';
      root.append(status);
      this.predictiveSearch.replaceChildren(root);
      this.predictiveSearch.hidden = false;
    }

    schedulePredictiveSearch() {
      if (!this.predictiveSearch || !this.searchInput) {
        return;
      }

      window.clearTimeout(this.predictiveSearchTimer);
      const query = this.searchInput.value.trim();

      if (query.length < 2) {
        this.clearPredictiveSearch();
        return;
      }

      this.showPredictiveLoading();
      this.predictiveSearchTimer = window.setTimeout(() => this.fetchPredictiveSearch(query), 180);
    }

    fetchPredictiveSearch(query) {
      const endpoint = this.predictiveSearch?.dataset.predictiveSearchUrl;

      if (!endpoint) {
        return;
      }

      if (this.predictiveSearchController) {
        this.predictiveSearchController.abort();
      }

      this.predictiveSearchController = new AbortController();
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');

      fetch(url.toString(), {
        headers: { 'Accept': 'text/html' },
        signal: this.predictiveSearchController.signal
      })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (!html || !this.predictiveSearch || this.searchInput.value.trim() !== query) {
            return;
          }

          const markup = this.extractPredictiveMarkup(html);

          if (!markup) {
            this.clearPredictiveSearch();
            return;
          }

          this.predictiveSearch.innerHTML = markup;
          this.predictiveSearch.hidden = false;
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            this.clearPredictiveSearch();
          }
        });
    }

    clearPredictiveSearch() {
      if (this.predictiveSearchController) {
        this.predictiveSearchController.abort();
        this.predictiveSearchController = null;
      }

      if (this.predictiveSearch) {
        this.predictiveSearch.innerHTML = '';
        this.predictiveSearch.hidden = true;
      }
    }

    updateCartCount(itemCount) {
      if (typeof itemCount === 'undefined' || Number.isNaN(Number(itemCount))) {
        return;
      }

      const count = Number(itemCount);

      if (this.cartCount) {
        this.cartCount.textContent = String(count);
        this.cartCount.hidden = count === 0;
      }

      if (this.cartLink) {
        this.cartLink.classList.toggle('has-items', count > 0);
        const template = this.cartLink.dataset.cartLabelTemplate || 'Cart, [count] items';
        this.cartLink.setAttribute('aria-label', template.replace('[count]', String(count)));
      }

      this.mobileCartCounts.forEach((cartCount) => {
        cartCount.textContent = String(count);
        cartCount.hidden = count === 0;
      });

      this.mobileCartLinks.forEach((cartLink) => {
        cartLink.classList.toggle('has-items', count > 0);
        const template = cartLink.dataset.cartLabelTemplate || 'Cart, [count] items';
        cartLink.setAttribute('aria-label', template.replace('[count]', String(count)));
      });
    }

    handleWishlistClick(event) {
      if (event.target.closest('[data-header-wishlist]')) {
        return;
      }

      const button = event.target.closest('[data-featured-products-wishlist]');

      if (!button || !this.header.contains(button)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (theme?.wishlist?.toggle) {
        theme.wishlist.toggle(button.dataset.productId, button.dataset.productHandle);
      }
    }

    syncWishlistButtons() {
      if (theme?.wishlist?.syncButtons) {
        theme.wishlist.syncButtons(this.header);
      }

      this.syncHeaderWishlist();
    }

    syncHeaderWishlist() {
      const buttons = this.header.querySelectorAll('[data-header-wishlist]');

      if (!buttons.length) {
        return;
      }

      const hasItems = Boolean(theme?.wishlist?.ids && theme.wishlist.ids().length);
      buttons.forEach((button) => {
        button.classList.toggle('is-active', hasItems);
        button.setAttribute('aria-current', hasItems ? 'true' : 'false');
      });
    }

    syncAccountState() {
      this.header.querySelectorAll('.header-account-link, [data-mobile-account]').forEach((account) => {
        const signedIn = account.classList.contains('is-signed-in')
          || account.getAttribute('data-signed-in') === 'true'
          || account.hasAttribute('signed-in')
          || account.getAttribute('aria-signed-in') === 'true';

        account.classList.toggle('is-signed-in', signedIn);

        if (signedIn) {
          account.setAttribute('data-signed-in', 'true');
        } else {
          account.removeAttribute('data-signed-in');
        }
      });
    }

    destroy() {
      this.closeDropdowns();
      this.closeSearch({ restoreFocus: false });
      this.closeMobileMenu({ restoreFocus: false });
      if (this.mobileSearchTimer) {
        window.clearTimeout(this.mobileSearchTimer);
      }
      this.cancelSearchHide();
      this.clearPredictiveSearch();
      this.restoreSearchOverlay();
      this.cleanup.forEach((callback) => callback());
      this.header.dataset.headerInitialized = 'false';
    }
  }

  const headerInstances = new WeakMap();
  const announcementInstances = new WeakMap();

  const initAnnouncements = (scope = document) => {
    scope.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
      if (bar.dataset.announcementInitialized === 'true') {
        return;
      }

      bar.dataset.announcementInitialized = 'true';
      announcementInstances.set(bar, new AnnouncementBar(bar));
    });
  };

  const initHeaders = (scope = document) => {
    scope.querySelectorAll('[data-header]').forEach((header) => {
      if (header.dataset.headerInitialized === 'true') {
        return;
      }

      header.dataset.headerInitialized = 'true';
      headerInstances.set(header, new Header(header));
    });
  };

  initAnnouncements();
  initHeaders();

  theme.events.on('section:load', (event) => {
    initAnnouncements(event.detail.target);
    initHeaders(event.detail.target);
  });

  theme.events.on('section:unload', (event) => {
    event.detail.target.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
      announcementInstances.get(bar)?.destroy();
      announcementInstances.delete(bar);
    });

    event.detail.target.querySelectorAll('[data-header]').forEach((header) => {
      headerInstances.get(header)?.destroy();
      headerInstances.delete(header);
    });
  });
})();
